import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { AssetCasesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { AssetCaseFilters } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { NewDialog } from './new_dialog'
import { ShowDialog } from './show_dialog'

interface Props extends TableProps {
  createable?: boolean
}

export const Table = ({ pageSize = 20, onIndex = false, createable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<AssetCasesQuery>(
    Query,
    { variables: { first: pageSize } }
  )
  const {
    pageInfo,
    setOpenNew,
    openNew,
    openShow,
    showShow,
    hideShow,
    resourceId,
  } = useTable({ pageInfo: data?.assetCases.pageInfo, onIndex, basePath: '/cases' })

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<AssetCaseFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        headings={[{ name: 'Barcode' }, { name: 'Asset count' }, { name: 'Description' }, { name: 'Warehouse' }]}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.assetCases.nodes = dedupe([
              ...previousData.assetCases.nodes,
              ...fetchMoreResult.assetCases.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 2,
            setValue: (set, value) => set(f => ({ ...f, descriptionEq: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
        ]}
        rows={data.assetCases.nodes.map(assetCase => (
          {
            id: assetCase.id,
            columns: [
              assetCase.barcode.barcode,
              assetCase.assetCount,
              assetCase.description,
              assetCase.warehouse?.name,
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <ShowDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />
    </>
  )
}
